<template>
  <UICard
    ref="cardRef"
    class="ui-transaction-info"
    :class="mainClasses"
    @click="handleClick"
  >
    <div
      v-if="!unclickable"
      class="ui-transaction-info__select"
      :class="selectClasses"
      @click.stop="handleClickSelect"
    >
      <span>
        <CheckIcon v-if="isSelected" aria-hidden="true" />
        {{ selectText }}
      </span>
    </div>
    <div ref="bodyRef" class="ui-transaction-info__body">
      <div class="ui-transaction-info__header">
        <div class="ui-transaction-info__header-main">
          <div class="ui-transaction-info__header-date">
            <slot name="date">
              <EditField
                v-if="instance"
                ref="dateRef"
                :instance
                method="getFormDate"
                display="displayDate"
                @edit="handleEdit"
              />
            </slot>
          </div>
          <div class="ui-transaction-info__header-type">
            <slot name="type">
              <EditField
                v-if="instance"
                ref="typeRef"
                :instance
                method="getFormType"
                :value="type"
                @edit="handleEdit"
              />
            </slot>
          </div>
          <div class="ui-transaction-info__header-state">
            <slot name="state">
              <span>{{ state }}</span>
            </slot>
          </div>
        </div>
        <div class="ui-transaction-info__header-description">
          <slot name="description">
            <EditField
              v-if="instance"
              ref="descriptionRef"
              :instance
              method="getFormDescription"
              :value="description"
              @edit="handleEdit"
            />
          </slot>
        </div>
        <div class="ui-transaction-info__header-state" :class="stateClasses">
          <slot name="state">
            <span>{{ state }}</span>
          </slot>
        </div>
      </div>
      <TransactionInfoEntries ref="entriesRef" :instance @edit="handleEdit">
        <template v-if="$slots.label" #label>
          <slot name="label" />
        </template>
        <template v-if="$slots.account" #account>
          <slot name="account" />
        </template>
        <template v-if="$slots.amount" #amount>
          <slot name="amount" />
        </template>
        <template v-if="$slots.asset" #asset>
          <slot name="asset" />
        </template>
      </TransactionInfoEntries>
    </div>
  </UICard>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'

import { TransactionClass } from '@types'

import { BREAKPOINTS } from './utils/const'
import { EMPTY_VALUE_PLACEHOLDER } from '@/const'

import TransactionInfoEntries from './TransactionInfoEntries.vue'
import EditField from './components/EditField.vue'
import { UICard } from '@ui/core'
import { onClickOutside } from '@vueuse/core'
import { CheckIcon } from '@heroicons/vue/24/outline'

type Props = {
  instance?: TransactionClass
  isActive?: boolean
  isSelected?: boolean
  unclickable?: boolean
}

type Emits = {
  click: [string]
  'click:select': [string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const cardRef = useTemplateRef<typeof UICard>('cardRef')
const bodyRef = useTemplateRef('bodyRef')
const dateRef = useTemplateRef('dateRef')
const typeRef = useTemplateRef('typeRef')
const descriptionRef = useTemplateRef('descriptionRef')
const entriesRef = useTemplateRef('entriesRef')

const observer = ref<ResizeObserver>()
const size = ref<'sm' | 'md'>()

const type = computed(
  () => props.instance?.field<string>('type').value || EMPTY_VALUE_PLACEHOLDER,
)

const description = computed(
  () =>
    props.instance?.field<string>('description').value ||
    EMPTY_VALUE_PLACEHOLDER,
)

const state = computed(() => props.instance?.state)

const mainClasses = computed(() => ({
  ['ui-transaction-info--active']: props.isActive,
  ['ui-transaction-info--unclickable']: props.unclickable,
  ['ui-transaction-info--typified']: !!props.instance?.entriesLength,
  [`ui-transaction-info--${size.value}`]: !!size.value,
}))

const stateClasses = computed(() => ({
  [`ui-transaction-info__header-state--${state.value}`]: state.value,
}))

const selectClasses = computed(() => ({
  ['ui-transaction-info__select--selected']: props.isSelected,
}))

const selectText = computed(() => (props.isSelected ? 'selected' : 'select'))

const handleClick = () => {
  props.instance && emit('click', props.instance.id)
}

const handleClickSelect = () => {
  props.instance && emit('click:select', props.instance.id)
}

const handleEdit = (field: string) => {
  dateRef.value?.reset(field)
  typeRef.value?.reset(field)
  descriptionRef.value?.reset(field)
  entriesRef.value?.reset(field)
}

onClickOutside(bodyRef, event => {
  const path =
    (event as any).path || (event.composedPath && event.composedPath())
  if (
    path.find((item: HTMLElement) =>
      item.classList?.contains('v-popper__popper'),
    )
  )
    return
  dateRef.value?.reset()
  typeRef.value?.reset()
  descriptionRef.value?.reset()
  entriesRef.value?.reset()
})

onMounted(() => {
  if (!cardRef.value) return
  observer.value = new ResizeObserver(entries => {
    const container = entries[0]
    if (!container) return
    const rect = container.target.getBoundingClientRect()
    const width = rect.width
    size.value = undefined
    if (width > BREAKPOINTS.SM && width <= BREAKPOINTS.MD) {
      size.value = 'md'
    } else if (width <= BREAKPOINTS.SM) {
      size.value = 'sm'
    }
  })
  observer.value.observe(cardRef.value.getEl())
})

onUnmounted(() => {
  observer.value?.disconnect()
})
</script>

<style>
.ui-transaction-info {
  @apply relative;
  @apply flex;
  @apply outline outline-1 outline-transparent;
  @apply bg-body;
  @apply border border-transparent;
  @apply text-sm sm:text-xs;
  @apply cursor-pointer;

  &--typified {
    @apply !pt-3;
  }

  &:not(&--unclickable) {
    @apply hover:outline-indigo-300 dark:hover:outline-indigo-600;
  }

  &--active {
    @apply !outline-indigo-300 dark:!outline-indigo-600;
  }

  &__select {
    @apply w-4 hover:w-6;
    @apply flex justify-center items-center;
    @apply -mt-3 -mb-4 -ml-4 mr-4;
    @apply rounded-l;
    @apply bg-body-gray;
    @apply text-gray-300 dark:text-gray-500;
    @apply transition-[width] duration-300;

    &--selected {
      @apply bg-primary-selected dark:bg-primary;
      @apply text-body dark:text-gray-400;
    }

    span {
      @apply flex items-center justify-center;
      @apply gap-0.5;
      @apply -rotate-90;
      @apply text-xs sm:text-[10px];
      @apply tracking-widest;
      @apply leading-none;

      svg {
        @apply size-3;
      }
    }
  }

  &__body {
    @apply flex-auto;
    @apply flex flex-col gap-3;
  }

  &__header {
    @apply flex items-start gap-1;
  }

  &--sm &__header {
    @apply flex-col gap-2;
  }

  &__header-state {
    @apply w-12;
    @apply flex justify-end;
    @apply text-gray-200 dark:text-gray-600;

    span {
      @apply inline-block;
      @apply px-1 py-0.5 -my-0.5 -mr-2;
      @apply bg-body-gray;
      @apply rounded;
    }

    &--add {
      @apply text-yellow-700 dark:text-yellow-500;
    }

    &--update {
      @apply text-yellow-500 dark:text-yellow-400;
    }

    &--invalid {
      @apply text-red-500 dark:text-red-400;
    }

    &--errors {
      @apply text-red-400 dark:text-red-300;
    }

    &--delete {
      @apply text-red-800 dark:text-red-700;
    }
  }

  &--sm &__header-state {
    @apply hidden;
  }

  &__header-main &__header-state {
    @apply hidden;
  }

  &__header-main {
    @apply flex;
    @apply gap-1;
  }

  &--sm &__header-main {
    @apply w-full;
  }

  &--sm &__header-main &__header-state {
    @apply flex;
    @apply ml-auto;
  }

  &__header-date {
    @apply w-[5.75rem];
    @apply font-medium;
    @apply text-base sm:text-sm leading-[1.15];

    .edit-field__input {
      @apply -ml-2;
    }
  }

  &__header-type {
    .edit-field:not(.edit-field--active) {
      @apply bg-primary-light dark:bg-gray-800;
      @apply px-2 py-1 -my-1;
      @apply rounded;
      @apply text-primary dark:text-indigo-400;
    }
  }

  &__header-description {
    @apply flex-1;
    @apply text-gray-400;
    @apply text-right;
  }
}
</style>
